import { useEffect, useMemo, useState } from 'react';
import {
  getPoolOffer,
  listPoolOffers,
  type ApiClient,
  type AvailableCategory,
} from '../api';

/** Mirrors the slider/tier list page size — a category with more packs than
    this could hide the deep-linked offer from the scan. Kept in sync with
    `useCategoryOffers`. */
const SCAN_PER_PAGE = 50;

/**
 * Resolves which category a deep-linked `game_id` belongs to.
 *
 * The header's `AvailableCategory.id` is the category's *representative*
 * pool_offer_id, so a plain `categories.find(c => c.id === gameId)` only
 * matches when the URL points at the representative pack. A deep link to any
 * *other* pack in the category (e.g. `?game_id=497`) fails that match, which
 * would leave the category unresolved — no slider, no tiers, no active pill.
 *
 * For those links the offer detail's `category_id` names the category with a
 * single request. The representative case still resolves synchronously from
 * `categories` with no request at all.
 */
export function useResolvedCategory(
  client: ApiClient,
  categories: AvailableCategory[],
  gameId: string | null,
): AvailableCategory | null {
  const direct = useMemo(() => {
    if (gameId === null) return null;
    return categories.find((c) => String(c.id) === gameId) ?? null;
  }, [categories, gameId]);

  const [resolved, setResolved] = useState<AvailableCategory | null>(direct);

  useEffect(() => {
    // Representative deep-link, or nothing to resolve yet — the direct match
    // is the answer and needs no network round-trip.
    if (direct || gameId === null || categories.length === 0) {
      setResolved(direct);
      return;
    }

    let cancelled = false;

    getPoolOffer(client, gameId)
      .then((offer) =>
        offer.categoryId === null
          ? findCategoryByScan(client, categories, Number(gameId))
          : (categories.find((c) => c.categoryId === offer.categoryId) ?? null),
      )
      .catch(() => null)
      .then((category) => {
        if (!cancelled) setResolved(category);
      });

    return () => {
      cancelled = true;
    };
  }, [client, categories, gameId, direct]);

  return resolved;
}

/**
 * Fallback for backends whose offer detail has no `category_id` yet (prod
 * until that backend change reaches master): fetch every category's offers
 * list and pick the one that contains the pack. Remove once prod ships it.
 */
async function findCategoryByScan(
  client: ApiClient,
  categories: AvailableCategory[],
  offerId: number,
): Promise<AvailableCategory | null> {
  const matches = await Promise.all(
    categories.map((category) =>
      listPoolOffers(client, {
        categoryId: category.categoryId,
        categoryClassName: category.categoryClassName,
        perPage: SCAN_PER_PAGE,
      })
        .then((offers) => (offers.some((offer) => offer.id === offerId) ? category : null))
        .catch(() => null),
    ),
  );
  return matches.find(Boolean) ?? null;
}
